import BaseStockProvider from '../BaseStockProvider';

/**
 * Pexels stock media provider
 * Free API: https://www.pexels.com/api/
 * 
 * Pexels API Guidelines:
 * 1. Attribute Pexels and the photographer
 * 2. Don't sell unmodified photos
 * 3. Don't create a competing service
 * 4. Display "Photo by [Photographer Name] on Pexels" or "Video by [Photographer Name] on Pexels"
 */
class PexelsProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Pexels',
      apiKey: apiKey || process.env.REACT_APP_PEXELS_API_KEY,
      baseUrl: 'https://api.pexels.com/v1',
      mediaTypes: ['image', 'video'],
      requiresAuth: true,
    });
  }

  /**
   * Search for media on Pexels
   */
  async search({ query, mediaType = 'image', page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Pexels API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const endpoint = mediaType === 'video' ? 'videos/search' : 'search';
      const response = await fetch(
        `${this.baseUrl}/${endpoint}?query=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}`,
        {
          headers: {
            Authorization: this.apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Pexels API error: ${response.status}`);
      }

      const data = await response.json();
      const items = mediaType === 'video' ? data.videos : data.photos;
      
      // Normalize and filter out invalid formats
      const normalizedItems = items.map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: data.page < Math.ceil(data.total_results / perPage),
        total: data.total_results,
        page: data.page,
      };
    } catch (error) {
      console.error('Pexels search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get popular/curated media
   */
  async getPopular({ mediaType = 'image', page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Pexels API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const endpoint = mediaType === 'video' ? 'videos/popular' : 'curated';
      const response = await fetch(
        `${this.baseUrl}/${endpoint}?page=${page}&per_page=${perPage}`,
        {
          headers: {
            Authorization: this.apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Pexels API error: ${response.status}`);
      }

      const data = await response.json();
      const items = mediaType === 'video' ? data.videos : data.photos;
      
      // Normalize and filter out invalid formats
      const normalizedItems = items.map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: items.length === perPage,
        total: null,
        page: page,
      };
    } catch (error) {
      console.error('Pexels getPopular error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Normalize Pexels item to common format
   */
  normalizeItem(item, mediaType) {
    if (mediaType === 'video') {
      return this.normalizeVideo(item);
    }
    return this.normalizePhoto(item);
  }

  /**
   * Normalize Pexels photo
   */
  normalizePhoto(item) {
    return {
      id: `pexels_photo_${item.id}`,
      provider: 'pexels',
      name: item.alt || 'Untitled',
      thumbnail: item.src.small,
      url: item.src.large,
      downloadUrl: item.src.original,
      type: 'image',
      category: 'Stock',
      description: item.alt,
      size: null,
      width: item.width,
      height: item.height,
      author: {
        name: item.photographer,
        username: item.photographer,
        profileUrl: item.photographer_url,
      },
      sourceUrl: item.url,
      attribution: `Photo by ${item.photographer} on Pexels`,
      attributionUrl: item.url,
      isUserUpload: false,
      color: item.avg_color,
    };
  }

  /**
   * Normalize Pexels video
   */
  normalizeVideo(item) {
    // Get the best quality video file
    const videoFile = item.video_files.find(f => f.quality === 'hd') || item.video_files[0];
    
    return {
      id: `pexels_video_${item.id}`,
      provider: 'pexels',
      name: 'Video',
      thumbnail: item.image,
      url: videoFile.link,
      downloadUrl: videoFile.link,
      type: 'video',
      category: 'Stock',
      description: '',
      size: videoFile.size ? `${(videoFile.size / (1024 * 1024)).toFixed(1)} MB` : null,
      width: videoFile.width,
      height: videoFile.height,
      duration: item.duration,
      author: {
        name: item.user.name,
        username: item.user.name,
        profileUrl: item.user.url,
      },
      sourceUrl: item.url,
      attribution: `Video by ${item.user.name} on Pexels`,
      attributionUrl: item.url,
      isUserUpload: false,
    };
  }

  /**
   * Get attribution text for display
   */
  getAttributionText(item, mediaType = 'image') {
    const type = mediaType === 'video' ? 'Video' : 'Photo';
    return item.attribution || `${type} by ${item.author?.name || 'Unknown'} on Pexels`;
  }
}

export default PexelsProvider;